import React, { useState, useMemo, useEffect, useRef, useCallback } from "react";
import { useNavigation } from "../../../hooks/useNavigation";
import { Search, LayoutGrid, List, CloudDownload } from "lucide-react";
import {
  Box,
  Grid,
  Typography,
  useTheme,
  Button,
  Drawer,
  useMediaQuery,
} from "@mui/material";
import { SlidersHorizontal } from "lucide-react";

import { getCategoryDisplayLabel, parseAmount } from "../../../utils/Utility";
import { useCodetablesSlice } from "../../../redux/slice/codetableSlice";
import MarketFilterSidebar, {
  FilterState,
} from "./MarketFilterSidebar";
import MarketCard from "./MarketCard";
import {
  getExploreMarketProducts,
  markAgreementRequestedForProduct,
  MarketProduct,
  resetMarketData,
  requestProductAgreement,
  setFavorites,
  getRequestedProductIds,
} from "../../../redux/slice/exploreMarket/exploreMarketSlice";
import { addRemovePreferenceValueAction, fetchUserPreference } from "../../../redux/slice/userSlice";
import { useAppDispatch, useAppSelector } from "../../../redux/slice/redux-hooks";
import { setSingleAlertObj, SingleAlertInfo } from "../../../redux/slice/commonSlice";
import OrderProductModal from "../account/OrderProductModal";
import { selectIsTokenAvailable, selectIsValueFoundInToken, useAuthSlice } from "../../../redux/slice/authSlice";
import { EXPORTABLE_ITEMS, PRODUCT_CATEGORY, USER_ROLES } from "../../../constants/applicationConstants";
import { AddCircleOutlineOutlined, ExitToApp, KeyboardArrowDown } from "@mui/icons-material";
import { useSelector } from "react-redux";
import { RootState } from "../../../redux/store";
import { useModalStyles } from "../../common/useModalStyles";
import StyledMenu from "../../common/StyledMenu";
import ExportToExcel from "../../common/ExportToExcel";
import PrescriptionModalPopup from "../prescription/PrescriptionModalPopup";
import { submitPrescription } from "../../../redux/slice/prescription/prescriptionSlice";
import InfoPopup from "../../common/InfoPopup";

// Helper function to check if user has admin access
const hasAdminAccess = (userInfo: any) => {
  if (userInfo && userInfo.userRole) {
    return userInfo.userRole === USER_ROLES.ROLE_SUPERADMIN || userInfo.userRole === USER_ROLES.ROLE_ADMIN;
  }
  return false;
};

const ExploreMarket: React.FC = () => {
  const { navigate } = useNavigation();
  const theme = useTheme();
  const { userInfo } = useAuthSlice();
  const isDark = theme.palette.mode === "dark";
  const isMobile = useMediaQuery(theme.breakpoints.down("lg"));
  const dispatch = useAppDispatch()
  const { marketData, fetchStatus } = useAppSelector((state) => state.exploreMarket);
  const { allCodeTablesValuesMap } = useCodetablesSlice();
  // State
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [viewMode, setViewMode] = useState<"grid" | "list">("grid");
  const [isFilterDrawerOpen, setIsFilterDrawerOpen] = useState(false);
  const [isMyProducts, setIsMyProducts] = useState(false);
  const [preferencesApplied, setPreferencesApplied] = useState(false);
  const [favoriteProductIds, setFavoriteProductIds] = useState<number[]>([]);
  const canCreateProduct = useSelector((state: RootState) => selectIsTokenAvailable("canCreateProduct")(state));
  const canExportExploreMarket = useSelector((state: RootState) =>
    selectIsValueFoundInToken("canAccessExportExcel", EXPORTABLE_ITEMS.EXPLORE_MARKET)(state)
  );
  const [anchorElMenu, setAnchorElMenu] = useState<null | HTMLElement>(null);
  const { primaryBtnSx, tokens } = useModalStyles();
  const [isOpenPrescription, setIsOpenPrescription] = useState(false);
  const [showInfoPopup, setShowInfoPopup] = useState<{message:string, show: boolean, title: string}>({message: "", show: false, title: "" });
  const [filters, setFilters] = useState<FilterState>({
    favoritesOnly: false,
    selectedCategories: [],
    selectedCompositions: [],
    selectedManufacturers: [],
    allowedStatuses: ["ACTIVE","TBD"],
    selectedPurchaseAndBill: [], // CS-1765
  });
const activeFiltersCount = useMemo(() => {
  let count = 0;

  if (filters.favoritesOnly) count++;

  count += filters.selectedCategories.length;
  count += filters.selectedCompositions.length;
  count += filters.selectedManufacturers.length;
  count += filters.allowedStatuses.filter((status) => !!status).length;
  if (hasAdminAccess(userInfo)) {
    count += filters.selectedPurchaseAndBill.length; // CS-1765
  }

  return count;
}, [filters, userInfo]);
  // const activeFiltersCount = useMemo(() => {
  //   let count = 0;
  //   if (filters.favoritesOnly) count++;
  //   count += filters.selectedCategories.length;
  //   count += filters.selectedCompositions.length;
  //   if (filters.allowableRateRange[0] > 0 || filters.allowableRateRange[1] < maxAllowableRate) count++;
  //   if (filters.mueRange[0] > 0 || filters.mueRange[1] < maxMue) count++;

  //   // Count status filtering as 1 group change if any status is selected
  //   if (filters.allowedStatuses.length > 0) {
  //     count++;
  //   }

  //   return count;
  // }, [filters, maxAllowableRate, maxMue]);

  // Modal state
  const [selectedProduct, setSelectedProduct] =
  useState<MarketProduct | null>(null);
  const [orderOpen, setOrderOpen] = useState(false);

  // Dynamic height calculation for sidebar alignment
  const sidebarRef = useRef<HTMLDivElement>(null);
  const [sidebarHeight, setSidebarHeight] = useState<number | string>("80vh");

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    if (!sidebarRef.current || isMobile) return;

    const resizeObserver = new ResizeObserver((entries) => {
      for (let entry of entries) {
        if (entry.target === sidebarRef.current) {
          setSidebarHeight(entry.contentRect.height);
        }
      }
    });

    resizeObserver.observe(sidebarRef.current);
    return () => resizeObserver.disconnect();
  }, [isMobile]);

  // Theme-aware colors
  const borderColor = isDark ? "rgba(148,163,184,0.2)" : "rgba(0,0,0,0.12)";
  const searchIconColor = isDark ? "#94a3b8" : "#637381";
  const searchTextColor = isDark ? "#ffffff" : "#161C24";
  const toggleActiveBg = isDark ? "#14b8a6" : "#007867";
  const toggleIconColor = isDark ? "#e2e8f0" : "#454F5B";
  const toggleActiveIconColor = "#ffffff";

  // Reset filters when toggling between Explore and My Products
  useEffect(() => {
    setFilters({
      favoritesOnly: false,
      selectedCategories: [],
      selectedCompositions: [],
      selectedManufacturers: [],
      allowedStatuses: ["ACTIVE","TBD"],
      selectedPurchaseAndBill: [], // CS-1765
    });
    setSearchTerm("");
  }, [isMyProducts]);

  // Fetch Products
  useEffect(() => {
    if (!userInfo?.userId) return;

    const fetchMarketProducts = async () => {
      try {
        setPreferencesApplied(false);
        dispatch(resetMarketData());
        setFavoriteProductIds([]);

        void dispatch(fetchUserPreference(userInfo.userId))
          .unwrap()
          .then((userPrefsResult) => {
            if (!userPrefsResult || userPrefsResult.length === 0) return;

            const favoriteProductsPreference = userPrefsResult.find(
              (pref: any) => pref.preferenceName === "favoriteProducts"
            );

            if (favoriteProductsPreference?.preferenceValue) {
              const favoriteIds = favoriteProductsPreference.preferenceValue
                .split(",")
                .map((id: any) => parseInt(id.trim()))
                .filter((id: any) => !isNaN(id));

              setFavoriteProductIds(favoriteIds);
            }
          })
          .catch(() => {
            // Favorites are a secondary overlay; keep the page usable even if this call fails.
          });

        await Promise.all([
          dispatch(getRequestedProductIds()).unwrap(),
          dispatch(getExploreMarketProducts(isMyProducts)).unwrap()
        ]);

        setPreferencesApplied(true);
      } catch (error) {
        const alertObj: SingleAlertInfo = {
          message: "Failed to load market products. Please try again later.",
          alertType: "error",
        };
        dispatch(setSingleAlertObj(alertObj));
        setPreferencesApplied(true);
      }
    };
    fetchMarketProducts();
  }, [dispatch, isMyProducts, userInfo?.userId]);

  useEffect(() => {
    if (!marketData.length) return;
    dispatch(setFavorites(favoriteProductIds));
  }, [dispatch, favoriteProductIds, marketData]);


  // Filtering
  const filteredProducts = useMemo(() => {
    if (!marketData || marketData.length === 0) return [];
    return marketData.filter((product) => {
      const term = (debouncedSearchTerm || "").toLowerCase();

      // Search term
      if (term) {
        const nameMatch = product.productName?.toLowerCase().includes(term);
        const mfgMatch = product.manufacturerName?.toLowerCase().includes(term);
        const careCategoryDisplay = getCategoryDisplayLabel(allCodeTablesValuesMap, product.productCategory);
        const careCategoryMatch = careCategoryDisplay?.toLowerCase().includes(term);
        const compositionMatch = product.composition?.toLowerCase().includes(term);
        if (!nameMatch && !mfgMatch && !careCategoryMatch && !compositionMatch) {
          return false;
        }
      }

      // Categories - CS-1760: Compare using raw keynames for stable matching
      if (filters.selectedCategories.length > 0) {
        const rawCat = (product.productCategory || "").trim().toUpperCase();
        if (!filters.selectedCategories.some(c => c.toUpperCase() === rawCat)) {
          return false;
        }
      }

      // Composition 
      if (filters.selectedCompositions.length > 0) {
        if (!product.composition || !filters.selectedCompositions.includes(product.composition)) {
          return false;
        }
      }

      // Manufacturer
      if (filters.selectedManufacturers.length > 0) {
        const manufacturerName = (product.manufacturerName || "").trim();
        if (!manufacturerName || !filters.selectedManufacturers.includes(manufacturerName)) {
          return false;
        }
      }

      // CS-1765 - Purchase & Bill filter is available only to admin and super admin users
      if (hasAdminAccess(userInfo) && filters.selectedPurchaseAndBill.length > 0) {
        const purchaseAndBill = (product.purchaseAndBill || "").trim().toUpperCase();
        if (!purchaseAndBill || !filters.selectedPurchaseAndBill.includes(purchaseAndBill)) {
          return false;
        }
      }

      // Favorites Only Filter
      if (filters.favoritesOnly) {
        if (!product.isFavorited) {
          return false;
        }
      }

      // Status 
      if (filters.allowedStatuses.length > 0 && filters.allowedStatuses[0] !== "") {
        if (!product.productStatus || !filters.allowedStatuses.includes(product.productStatus)) {
          return false;
        }
      }

      return true;
    });
  }, [marketData, debouncedSearchTerm, filters, userInfo, allCodeTablesValuesMap]);

  // Handlers
  const handleToggleFavorite = useCallback((productId: number) => {
    const data = {
      preferenceName: "favoriteProducts",
      value: productId.toString(),
      skipLoader: false,
    };
    dispatch(addRemovePreferenceValueAction(data)).then((res)=>{
       const preferenceValue = res?.payload?.data?.preferenceValue || "";
       const favoriteIds = preferenceValue
        ? preferenceValue.split(',')
            .map((id:any) => parseInt(id.trim()))
            .filter((id:any) => !isNaN(id))
        : [];
        setFavoriteProductIds(favoriteIds);
        dispatch(setFavorites(favoriteIds));
    }).catch((error) => {
      const alertObj: SingleAlertInfo = {
        message: "Failed to update favorite status. Please try again.",
        alertType: "error",
      };
      dispatch(setSingleAlertObj(alertObj));
    });
  }, [dispatch]);

  const handleCardClick = useCallback((productId: string) => {
    navigate(`/product?id=${productId}`);
  }, [navigate]);

  // CS-1731: Request agreement handler with preference overlay update
  const handleRequestAgreement = useCallback(async (
    product: MarketProduct
  ) => {
    const practiceId = Number(userInfo?.entityIds?.[0] || 0);
    if (!practiceId) {
      const alertObj: SingleAlertInfo = {
        message: "Practice information is not available to request agreement.",
        alertType: "error",
      };
      await dispatch(setSingleAlertObj(alertObj));
      return;
    }

    await dispatch(requestProductAgreement({ practiceId, productId: product.productId }))
      .unwrap()
      .then(async () => {
        // CS-1747: Backend already creates agreement_request row — just update UI state
        dispatch(markAgreementRequestedForProduct({ productId: product.productId, requested: true }));
        setSelectedProduct((prev) =>
          prev && prev.productId === product.productId
            ? { ...prev, isAgreementRequestedByUser: true }
            : prev
        );

        const alertObj: SingleAlertInfo = {
          message: `Agreement requested for ${product.productName}`,
          alertType: "success",
        };
        await dispatch(setSingleAlertObj(alertObj));
      })
      .catch(() => {
        const alertObj: SingleAlertInfo = {
          message: `Failed to request agreement for ${product.productName}.`,
          alertType: "error",
        };
        dispatch(setSingleAlertObj(alertObj));
      });
  }, [dispatch, userInfo?.entityIds]);

  const handleOrderProduct = useCallback((
    product: MarketProduct
  ) => {
    setSelectedProduct(product);
    setOrderOpen(true);
  }, []);

  const handlePrescribeProduct = (product: MarketProduct) => {
    setSelectedProduct({
      ...product,
      extendedPrice: product.extendedPrice ?? null,
      hasSKUPrice: product.hasSKUPrice ?? false,
      skuAllowablePrices: product.skuAllowablePrices ?? [],
    });
    setIsOpenPrescription(true);
  };

  const handleFavoriteClick = useCallback((productId: string) => {
    handleToggleFavorite(Number(productId));
  }, [handleToggleFavorite]);

  const handleRequestAgreementClick = useCallback((productId: string) => {
    const product = marketData.find((p) => String(p.productId) === productId);
    if (product) {
      handleRequestAgreement(product);
    }
  }, [marketData, handleRequestAgreement]);

  const handleOrderProductClick = useCallback((productId: string) => {
    const product = marketData.find((p) => String(p.productId) === productId);
    const isStandardOrGeneralCategory = product?.productCategory === PRODUCT_CATEGORY.STANDARD || product?.productCategory === PRODUCT_CATEGORY.GENERAL;
    if(isStandardOrGeneralCategory) {
      handlePrescribeProduct(product);
    } else if (product) {
      handleOrderProduct(product);
    }
  }, [marketData, handleOrderProduct]);

  const handleClearFilters = () => {
    setFilters({
      favoritesOnly: false,
      selectedCategories: [],
      selectedCompositions: [],
      selectedManufacturers: [],
      allowedStatuses: [],
      selectedPurchaseAndBill: [], // CS-1765
    });
    setSearchTerm("");
  };

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => {
          setAnchorElMenu(event.currentTarget);
      };
  
      const handleMenuClose = () => {
          setAnchorElMenu(null);
      };
  
  // CS-1728 - Explore Market export using shared ExportToExcel utility (in-memory filtered data, no API call)
  const totalRecords = filteredProducts.length;

  const handleExport = async () => {
    if (filteredProducts.length === 0) {
      const alertObj: SingleAlertInfo = { message: "No products to export.", alertType: "info" };
      dispatch(setSingleAlertObj(alertObj));
      handleMenuClose();
      return;
    }
    ExportToExcel({ records: filteredProducts }, [], dispatch, EXPORTABLE_ITEMS.EXPLORE_MARKET);
    handleMenuClose();
  };

  const handleSubmitPrescription = async (payload: any): Promise<any[] | null> => {
    if (payload) {
      const result = await dispatch(submitPrescription({ request: payload, skipLoader: false }));
      if (result?.payload?.data.length === 0) {
        setShowInfoPopup({ message: result?.payload?.message, show: true, title: "Error Occurred While Submitting the Rx Request" })
      }
      return result?.payload?.data ?? null;
    }
    return null;
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        px: { xs: 1, md: 3 },
        py: 2
      }}
    >
      {/* <Box
        sx={{
          overflow: "hidden",
          mb: 2,
          borderRadius: 1.5,
          backgroundColor:
            theme.palette.mode === "dark"
              ? "rgba(20, 184, 166, 0.06)"
              : "rgba(20, 184, 166, 0.05)",
          border: `1px solid ${theme.palette.mode === "dark"
            ? "rgba(20,184,166,0.12)"
            : "rgba(20,184,166,0.1)"
            }`,
          py: 0.6,
        }}
      >
        <Typography
          component="div"
          sx={{
            display: "inline-block",
            whiteSpace: "nowrap",
            color: "#14b8a6",
            fontSize: "0.82rem",
            fontWeight: 500,
            animation: "marqueeScroll 25s linear infinite",
            "@keyframes marqueeScroll": {
              "0%": { transform: "translateX(100vw)" },
              "100%": { transform: "translateX(-100%)" },
            },
          }}
        >
          🚧 &nbsp; This page is currently under development. Some features may not be available or function correctly. &nbsp;&nbsp;&nbsp; 🚧 &nbsp; This page is currently under development. Some features may not be available or function correctly.
          &nbsp;&nbsp;&nbsp; 🚧
        </Typography>
      </Box> */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 2,
        }}
      >
        <Box>
          {/* Toggle between Explore and My Products */}
          <Box
            sx={{
              display: "flex",
              backgroundColor: isDark ? "rgba(30,41,59,0.5)" : "rgba(255,255,255,0.8)",
              borderRadius: "12px",
              p: "4px",
              border: `1px solid ${borderColor}`,
              position: "relative",
              minWidth: "260px",
              height: "42px",
            }}
          >
            <Box
              sx={{
                position: "absolute",
                top: "4px",
                bottom: "4px",
                left: isMyProducts ? "50%" : "4px",
                width: "calc(50% - 4px)",
                backgroundColor: toggleActiveBg,
                borderRadius: "8px",
                transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
              }}
            />
            <Box
              onClick={() => setIsMyProducts(false)}
              sx={{
                flex: 1,
                textAlign: "center",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "13px",
                fontWeight: 600,
                zIndex: 1,
                cursor: "pointer",
                color: !isMyProducts ? "#fff" : isDark ? "#94a3b8" : "#637381",
                transition: "color 0.3s ease",
              }}
            >
              Explore Market
            </Box>
            <Box
              onClick={() => setIsMyProducts(true)}
              sx={{
                flex: 1,
                textAlign: "center",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "13px",
                fontWeight: 600,
                zIndex: 1,
                cursor: "pointer",
                color: isMyProducts ? "#fff" : isDark ? "#94a3b8" : "#637381",
                transition: "color 0.3s ease",
              }}
            >
              My Products
            </Box>
          </Box>
          <Typography variant="subtitle1" color="text.secondary" sx={{ mt: 1 }}>
            {isMyProducts ? "View and manage your selected products portfolio" : "Discover wound care products and expand your portfolio"}
          </Typography>
        </Box>
        {/* CS-1728 : Add Product + Export buttons side by side */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap", justifyContent: "flex-end" }}>
          {canCreateProduct && (
            <Button
              variant="contained"
              startIcon={<AddCircleOutlineOutlined />}
              onClick={() => navigate("/product")}
              sx={{ ...primaryBtnSx, whiteSpace: "nowrap" }}
            >
              Add Products
            </Button>
          )}
          {canExportExploreMarket && (
            <>
              <Button
                id="customized-button"
                aria-controls={Boolean(anchorElMenu) ? 'customized-menu' : undefined}
                aria-haspopup="true"
                aria-expanded={Boolean(anchorElMenu) ? 'true' : undefined}
                variant="outlined"
                disableElevation
                onClick={handleMenuClick}
                startIcon={<ExitToApp />}
                endIcon={
                  <Box
                    component="span"
                    sx={{
                      display: 'flex',
                      transition: 'transform 0.3s ease',
                      transform: anchorElMenu ? 'rotate(180deg)' : 'rotate(0deg)',
                      padding: '2px',
                    }}
                  >
                    <KeyboardArrowDown />
                  </Box>
                }
                sx={{
                  textTransform: "none",
                  fontWeight: 700,
                  fontSize: "0.9rem",
                  borderRadius: "10px",
                  py: 1.2,
                  px: 2,
                  color: tokens.accent,
                  borderColor: "rgba(20,184,166,0.55)",
                  backgroundColor: isDark ? "rgba(20,184,166,0.08)" : "rgba(20,184,166,0.04)",
                  boxShadow: "none",
                  whiteSpace: "nowrap",
                  "& .MuiButton-startIcon": {
                    mr: 0.75,
                    "& svg": { fontSize: "18px" },
                  },
                  "& .MuiButton-endIcon": {
                    ml: 0.5,
                    "& svg": { fontSize: "20px" },
                  },
                  "&:hover": {
                    color: "#fff",
                    borderColor: tokens.accentHover,
                    backgroundColor: tokens.accentHover,
                    boxShadow: "0 6px 22px rgba(20,184,166,0.32)",
                  },
                  "&:focus-visible": {
                    outline: "none",
                    boxShadow: "0 0 0 3px rgba(20,184,166,0.24)",
                  },
                }}
              >
                Export
              </Button>

              <StyledMenu
                id="customized-menu"
                MenuListProps={{
                  'aria-labelledby': 'customized-button',
                  sx: { p: 0.75 },
                }}
                anchorEl={anchorElMenu}
                open={Boolean(anchorElMenu)}
                onClose={handleMenuClose}
                slotProps={{
                  paper: {
                    sx: {
                      width: 230,
                      maxWidth: '90vw',
                      mt: 0.75,
                      borderRadius: '10px',
                      border: `1px solid ${isDark ? 'rgba(20,184,166,0.35)' : 'rgba(20,184,166,0.28)'}`,
                      backgroundColor: isDark ? '#141720' : '#ffffff',
                      backgroundImage: 'none',
                      boxShadow: isDark
                        ? '0 18px 42px rgba(0,0,0,0.45)'
                        : '0 14px 32px rgba(15,23,42,0.14)',
                    },
                  },
                }}
              >
                <Box>
                  <Button
                    fullWidth
                    variant="outlined"
                    startIcon={<CloudDownload />}
                    onClick={() => handleExport()}
                    disabled={totalRecords > 1000}
                    sx={{
                      justifyContent: 'flex-start',
                      textTransform: 'none',
                      fontWeight: 700,
                      fontSize: '0.9rem',
                      borderRadius: '8px',
                      py: 1,
                      px: 1.5,
                      color: tokens.accent,
                      borderColor: 'rgba(20,184,166,0.45)',
                      backgroundColor: isDark ? 'rgba(20,184,166,0.08)' : 'rgba(20,184,166,0.04)',
                      '& .MuiButton-startIcon': {
                        mr: 1,
                        '& svg': { fontSize: '19px' },
                      },
                      '&:hover': {
                        color: '#fff',
                        borderColor: tokens.accentHover,
                        backgroundColor: tokens.accentHover,
                      },
                      '&.Mui-disabled': {
                        color: isDark ? 'rgba(148,163,184,0.55)' : 'rgba(100,116,139,0.7)',
                        borderColor: isDark ? 'rgba(148,163,184,0.18)' : 'rgba(100,116,139,0.24)',
                        backgroundColor: isDark ? 'rgba(148,163,184,0.06)' : 'rgba(100,116,139,0.06)',
                      },
                    }}
                  >
                    Export Search List
                  </Button>
                </Box>
                {totalRecords > 1000 && (
                  <Box sx={{ mx: 1, p: 1.5, mt: 1, backgroundColor: 'warning.light', borderRadius: 1, color: 'warning.contrastText' }}>
                    <Typography variant="body2">
                      Export is available only when the list contains 1,000 records or fewer.
                    </Typography>
                  </Box>
                )}
              </StyledMenu>
            </>
          )}
        </Box>
      </Box>
      

      {/* Toolbar */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 2,
        }}
      >
        {/* Search */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            px: 2,
            flex: 1,
            height: "44px",
            borderRadius: "12px",
            border: `1px solid ${borderColor}`,
            backgroundColor: isDark ? "rgba(20,24,33,0.6)" : "rgba(255,255,255,0.85)",
            boxShadow: isDark ? "0 2px 10px rgba(0,0,0,0.16)" : "0 2px 8px rgba(15,23,42,0.04)",
            transition: "border-color 0.18s ease, box-shadow 0.18s ease",
            "&:hover": {
              borderColor: isDark ? "rgba(148,163,184,0.35)" : "rgba(0,0,0,0.2)",
            },
            "&:focus-within": {
              borderColor: "#14b8a6",
              boxShadow: `0 0 0 3px ${isDark ? "rgba(20,184,166,0.18)" : "rgba(20,184,166,0.14)"}`,
            },
          }}
        >
          <Search size={17} color={searchIconColor} style={{ flexShrink: 0 }} />

          <Box
            component="input"
            value={searchTerm}
            onChange={(e) =>
              setSearchTerm(e.target.value)
            }
            placeholder="Search by product name, manufacturer, care category, or composition"
            sx={{
              background: "transparent",
              border: "none",
              outline: "none",
              color: searchTextColor,
              ml: 1.25,
              width: "100%",
              fontSize: 14,
              height: "22px",
              fontFamily: "inherit",
              "&::placeholder": {
                color: isDark ? "rgba(148,163,184,0.65)" : "rgba(100,116,139,0.75)",
                opacity: 1,
              },
            }}
          />
        </Box>

        {/* Filter Toggle Button (Visible on mobile/tablet or per prototype) */}
        <Button
          onClick={() => setIsFilterDrawerOpen(true)}
          startIcon={<SlidersHorizontal size={18} />}
          sx={{
            borderRadius: 2,
            border: `1px solid ${borderColor}`,
            backgroundColor: isDark ? "rgba(30,41,59,0.5)" : "rgba(255,255,255,0.8)",
            color: "text.primary",
            px: 2,
            py: 1,
            textTransform: "none",
            fontSize: 13,
            fontWeight: 600,
            display: isMobile ? "flex" : "none",
            gap: 1,
            "&:hover": {
              backgroundColor: isDark ? "rgba(148,163,184,0.1)" : "rgba(0,0,0,0.06)",
              borderColor: isDark ? "rgba(148,163,184,0.4)" : "rgba(0,0,0,0.2)",
            }
          }}
        >
          Filters
          {activeFiltersCount > 0 && (
            <Box
              sx={{
                width: 18,
                height: 18,
                borderRadius: "50%",
                backgroundColor: "#14b8a6",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 10,
                color: "white",
                fontWeight: 700,
              }}
            >
              {activeFiltersCount}
            </Box>
          )}
        </Button>

        {/* Toggle */}
        <Box display="flex" gap={0.5}>
          <Box
            p={1}
            sx={{
              cursor: "pointer",
              borderRadius: 1,
              backgroundColor:
                viewMode === "grid"
                  ? toggleActiveBg
                  : "transparent",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              transition: "all 0.2s ease",
              "&:hover": {
                backgroundColor:
                  viewMode === "grid"
                    ? toggleActiveBg
                    : isDark
                      ? "rgba(148,163,184,0.1)"
                      : "rgba(0,0,0,0.06)",
              },
            }}
            onClick={() =>
              setViewMode("grid")
            }
          >
            <LayoutGrid
              size={18}
              color={
                viewMode === "grid"
                  ? toggleActiveIconColor
                  : toggleIconColor
              }
            />
          </Box>

          <Box
            p={1}
            sx={{
              cursor: "pointer",
              borderRadius: 1,
              backgroundColor:
                viewMode === "list"
                  ? toggleActiveBg
                  : "transparent",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              transition: "all 0.2s ease",
              "&:hover": {
                backgroundColor:
                  viewMode === "list"
                    ? toggleActiveBg
                    : isDark
                      ? "rgba(148,163,184,0.1)"
                      : "rgba(0,0,0,0.06)",
              },
            }}
            onClick={() =>
              setViewMode("list")
            }
          >
            <List
              size={18}
              color={
                viewMode === "list"
                  ? toggleActiveIconColor
                  : toggleIconColor
              }
            />
          </Box>
        </Box>
      </Box>

      {/* Layout */}
      <Box display="flex" gap={2} sx={{ flex: 1, minHeight: 0, mt: 2 }}>
        {/* Sidebar (Desktop) */}
        {!isMobile && (
          <Box
            ref={sidebarRef}
            width={280}
            sx={{
              flexShrink: 0,
              marginBottom: 4,
            }}
          >
            <MarketFilterSidebar
              filters={filters}
              onFiltersChange={setFilters}
              products={marketData}
              onClearAll={handleClearFilters}
              activeFiltersCount={activeFiltersCount}
            />
          </Box>
        )}

        {/* Filter Drawer (Mobile) */}
        <Drawer
          anchor="left"
          open={isFilterDrawerOpen}
          onClose={() => setIsFilterDrawerOpen(false)}
          PaperProps={{
            sx: {
              width: 320,
              backgroundColor: isDark ? "rgba(30,41,59,0.8)" : "#fff",
              backdropFilter: "blur(12px)",
              backgroundImage: "none",
              p: 3,
            }
          }}
        >
          <MarketFilterSidebar
            filters={filters}
            onFiltersChange={setFilters}
            products={marketData}
            hideStyle={true}
            onClearAll={handleClearFilters}
            activeFiltersCount={activeFiltersCount}
            onClose={() => setIsFilterDrawerOpen(false)}
          />
        </Drawer>

        {/* Products */}
        <Box
          flex={1}
          sx={{
            minHeight: "500px",
            height: "fit-content",
            overflowY: "auto",
            maxHeight: sidebarHeight,
            pr: 1,
            scrollbarColor: isDark ? "#14b8a6 rgba(255,255,255,0.06)" : "#0d9488 rgba(15,23,42,0.05)",
            scrollbarWidth: "thin",
            "&::-webkit-scrollbar": {
              width: "8px",
            },
            "&::-webkit-scrollbar-track": {
              backgroundColor: isDark ? "rgba(255,255,255,0.04)" : "rgba(15,23,42,0.03)",
              borderRadius: "10px",
            },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: isDark ? "rgba(20,184,166,0.55)" : "rgba(13,148,136,0.5)",
              borderRadius: "10px",
              border: isDark ? "2px solid rgba(20,24,33,0.6)" : "2px solid rgba(255,255,255,0.85)",
              backgroundClip: "padding-box",
            },
            "&::-webkit-scrollbar-thumb:hover": {
              backgroundColor: isDark ? "rgba(20,184,166,0.8)" : "rgba(13,148,136,0.75)",
            },
          }}
        >
          <Typography mb={2} color="text.primary" fontWeight={500}>
            {fetchStatus === "loading" ? (isMyProducts ? "Fetching your products..." : "Discovering products...") : !preferencesApplied ? "Loading..." : `${filteredProducts.length} products found`}
          </Typography>

          {filteredProducts.length === 0 && (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                py: 10,
                opacity: 0.8
              }}
            >
              <Box
                sx={{
                  backgroundColor: isDark ? "rgba(148,163,184,0.1)" : "rgba(0,0,0,0.05)",
                  borderRadius: "50%",
                  p: 3,
                  mb: 2
                }}
              >
                <Search size={48} color={isDark ? "#94a3b8" : "#637381"} />
              </Box>
              <Typography variant="h5" fontWeight={600} color="text.primary">
                No products found
              </Typography>
              <Typography color="text.secondary" mt={1}>
                {fetchStatus === "loading" ? (isMyProducts ? "Searching your collection..." : "Searching the market...") : "Try adjusting your filters or search term"}
              </Typography>
            </Box>
          )}

          {viewMode === "grid" && filteredProducts.length > 0 && (
            <Grid
              container
              spacing={2}
              mb={4}
            >
              {filteredProducts.map(
                (product) => (
                  <Grid
                    item
                    xs={12}
                    sm={6}
                    md={6}
                    lg={4}
                    key={
                      product.productId
                    }
                  >
                    <MarketCard
                      id={String(
                        product.productId
                      )}
                      title={
                        product.productName
                      }
                      company={
                        product.manufacturerName || "NA"
                      }
                      price={parseAmount(product.allowableRate ?? 0)}
                      listPrice={
                        product.listPrice != null
                          ? parseAmount(product.listPrice)
                          : null
                      }
                      composition={
                        product.composition || ""
                      }
                      isFavorite={
                        product.isFavorited
                      }
                      agreementRequired={
                        product.isAgreementRequired || false
                      }
                      agreementRequested={
                        !!product.isAgreementRequestedByUser
                      }
                      agreementApproved={
                        product.agreementStatus ===
                        "APPROVED"
                      }
                      productCategory={product.productCategory}
                      agreementStatus={
                        product.agreementStatus
                      }
                      productStatus={product.productStatus}
                      skuAllowablePrices={product.skuAllowablePrices}
                      macPrices={product.macPrices}
                      extendedPrice={product.extendedPrice}
                      productConfigs={product.productConfigs}
                      manufacturerId={product.manufacturerId}
                      onFavoriteClick={handleFavoriteClick}
                      onRequestAgreement={handleRequestAgreementClick}
                      onOrderProduct={handleOrderProductClick}
                      onProductClick={handleCardClick}
                    />
                  </Grid>
                )
              )}
            </Grid>
          )}

          {viewMode === "list" && filteredProducts.length > 0 && (
            <Box display="flex" flexDirection="column" gap={1.5} mb={4}>
              {filteredProducts.map(
                (product) => (
                  <MarketCard
                    key={product.productId}
                    id={String(
                      product.productId
                    )}
                    title={
                      product.productName
                    }
                    company={
                      product.manufacturerName || "NA"
                    }
                    price={parseAmount(product.allowableRate ?? 0)}
                    listPrice={
                      product.listPrice != null
                        ? parseAmount(product.listPrice)
                        : null
                    }
                    composition={
                      product.composition || ""
                    }
                    isFavorite={
                      product.isFavorited
                    }
                    agreementRequired={
                      product.isAgreementRequired || false
                    }
                    agreementRequested={
                      !!product.isAgreementRequestedByUser
                    }
                    agreementApproved={
                      product.agreementStatus ===
                      "APPROVED"
                    }
                    agreementStatus={
                      product.agreementStatus
                    }
                    productStatus={product.productStatus}
                    productCategory={product.productCategory}
                    viewMode="list"
                    skuAllowablePrices={product.skuAllowablePrices}
                    macPrices={product.macPrices}
                    extendedPrice={product.extendedPrice}
                    productConfigs={product.productConfigs}
                    manufacturerId={product.manufacturerId}
                    onFavoriteClick={handleFavoriteClick}
                    onRequestAgreement={handleRequestAgreementClick}
                    onOrderProduct={handleOrderProductClick}
                    onProductClick={handleCardClick}
                  />
                )
              )}
            </Box>
          )}
        </Box>
      </Box>

      {/* CS-1732: Order Product Modal */}
      <OrderProductModal
        open={orderOpen}
        onClose={() => setOrderOpen(false)}
        product={selectedProduct ? {
          productId: selectedProduct.productId,
          productName: selectedProduct.productName,
          manufacturerId: selectedProduct.manufacturerId,
          manufacturerName: selectedProduct.manufacturerName || "N/A",
          productCategory: selectedProduct.productCategory || undefined,
          extendedPrice: selectedProduct.extendedPrice ?? null,
          skuAllowablePrices: selectedProduct.skuAllowablePrices ?? null,
          hasSKUPrice:selectedProduct.hasSKUPrice
        } : undefined}
      />

      { isOpenPrescription && selectedProduct &&

        <PrescriptionModalPopup
          product={selectedProduct}
          isOpen={isOpenPrescription}
          fromExploreMarket
          onClose={(closedAfterSuccessfulSubmit) => {
            setIsOpenPrescription(false);
            if (closedAfterSuccessfulSubmit) {
              navigate("/prescription_list");
            }
          }}
          onSubmit={handleSubmitPrescription}
        />
      }

      {showInfoPopup.show &&
        <InfoPopup
          open={showInfoPopup.show}
          content={showInfoPopup.message}
          onClose={() => setShowInfoPopup({ message: "", show: false, title: "" })}
          severity="error"
          title={showInfoPopup.title}
        />
      }
    </Box>
  );
};

export default ExploreMarket;
